import React, { useEffect, useState } from 'react';
import IEvaluationFormStep from 'interfaces/IEvaluationFormStep';
import { Accordion, FormGroup, Row } from 'react-bootstrap';
import AccordionItem from './AccordionItem';
import _ from 'lodash';
import ISimpleNameValuePair from '../../../../interfaces/ISimpleNameValuePair';
import AlgorithmLegend from '../AlgorithmLegend';
import HelpButton from '../../../Form/HelpButton';
import { ReviewFormat } from '../../../../graphql/__generated__/graphql';
import { AccordionEventKey } from 'react-bootstrap/esm/AccordionContext';
import { IEvaluationRequirementsGroup } from '../../../../interfaces/IApplicationRequirements';

function mergeRequirementsAndData(
    requirementsGroup: IEvaluationRequirementsGroup,
    newPartData: ISimpleNameValuePair[]
) {
    const partBMerged = _.merge(
        {},
        _.keyBy(requirementsGroup.requirements, 'name'),
        _.keyBy(newPartData, 'name')
    );
    return _.sortBy(_.values(partBMerged), 'sortKey');
}

const AccordionInput: React.FC<IEvaluationFormStep> = ({
    formData,
    onFeedback,
    requirementsGroup,
    onInputChange,
    readOnly = false,
    requirements,
    reviewFormat,
}) => {
    const partData: ISimpleNameValuePair[] = formData[requirementsGroup.name] || [];
    const help = requirements.config?.help && requirements.config?.help[requirementsGroup.name];

    // Track the currently active accordion item
    const [activeKey, setActiveKey] = useState<AccordionEventKey | null>(null);

    // Initialize active key to the first incomplete item
    useEffect(() => {
        const mergedReqs = mergeRequirementsAndData(requirementsGroup, partData);
        const firstIncomplete = mergedReqs.find((r) => !r.value?.complete)?.name;
        setActiveKey(firstIncomplete || null);
    }, []);

    useEffect(() => {
        const mergedReqs = mergeRequirementsAndData(requirementsGroup, partData);
        // Find the index of the current item
        const currentIndex = mergedReqs.findIndex((req) => req.name === activeKey);

        const firstIncomplete = mergedReqs
            .slice(currentIndex)
            .find((r) => !r.value?.complete)?.name;

        // If there's a next item, set it as active
        if (currentIndex !== -1 && currentIndex < mergedReqs.length - 1) {
            setActiveKey(firstIncomplete);
        }
    }, [requirementsGroup.requirements, partData]);

    const handleOnChange = (name: string, value: any) => {
        const newPartData = _.unionBy([{ name, value }], partData, 'name');
        onInputChange(requirementsGroup.name, newPartData);
    };

    const mergedReqs = mergeRequirementsAndData(requirementsGroup, partData);
    const isAlgorithm = reviewFormat === ReviewFormat.Algorithm;
    const sectionComplete = partData.every((b) => b.value?.complete);

    return (
        <Row>
            <h2>
                {requirementsGroup.pageTitle}{' '}
                {sectionComplete && (
                    <i
                        className={'bi bi-check2-circle ms-2 fs-4'}
                        title={`${requirementsGroup.pageTitle} complete`}
                    />
                )}
                {help && (
                    <HelpButton title={requirementsGroup.pageTitle} content={help}>
                        {isAlgorithm && <AlgorithmLegend />}
                    </HelpButton>
                )}
            </h2>
            <p className={'text-black'}>{requirementsGroup.pageSubtitle}</p>
            <FormGroup>
                <Accordion
                    activeKey={activeKey}
                    onSelect={(key) => setActiveKey(key)}
                    className={'bg-transparent'}
                >
                    {mergedReqs.map((req) => (
                        <AccordionItem
                            key={req.name}
                            req={req}
                            readOnly={readOnly}
                            onChange={handleOnChange}
                            value={req.value}
                            handleFeedback={onFeedback}
                            isAlgorithm={isAlgorithm}
                        />
                    ))}
                </Accordion>
            </FormGroup>
        </Row>
    );
};

export default AccordionInput;
